"use client";

import { Coins, FileClock, Mail, Smartphone, UserRoundPlus } from "lucide-react";
import { useRouter } from "next/navigation";
import { FormEvent, useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { getLoginPolicyNotice } from "@/content/policies";

type ApiResult = {
  ok: boolean;
  code?: string;
  message?: string;
  challengeId?: string;
  debugCode?: string;
  policies?: Array<{
    kind: "terms" | "privacy";
    language: "zh" | "en";
    version: string;
    launchReady: boolean;
    href: string;
  }>;
};

export function MemberLoginForm({
  nextPath = "/member",
}: {
  nextPath?: string;
} = {}) {
  const router = useRouter();
  const [email, setEmail] = useState("");
  const [code, setCode] = useState("");
  const [challengeId, setChallengeId] = useState("");
  const [debugCode, setDebugCode] = useState("");
  const [policies, setPolicies] = useState<ApiResult["policies"]>([]);
  const [consentAccepted, setConsentAccepted] = useState(false);
  const [pending, setPending] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [message, setMessage] = useState("");
  const [tone, setTone] = useState<"error" | "success">("error");
  const policyNotice = getLoginPolicyNotice(policies);
  const policyBundlePending = policies?.length !== 2;

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setInterval(
      () => setCountdown((value) => Math.max(0, value - 1)),
      1_000,
    );
    return () => window.clearInterval(timer);
  }, [countdown]);

  async function requestCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (pending || countdown > 0) return;
    setPending(true);
    setMessage("");
    try {
      const response = await fetch("/api/auth/email/request", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const result = (await response.json()) as ApiResult;
      if (!result.ok || !result.challengeId || result.policies?.length !== 2) {
        setTone("error");
        setMessage(
          result.message ||
            (result.code === "RATE_LIMITED"
              ? "发送过于频繁，请稍后再试。"
              : "请输入有效的邮箱地址。"),
        );
        return;
      }
      setChallengeId(result.challengeId);
      setDebugCode(result.debugCode || "");
      setPolicies(result.policies);
      setCountdown(60);
      setTone("success");
      setMessage("验证码已发送，请检查邮箱。");
    } catch {
      setTone("error");
      setMessage("验证码发送失败，请稍后再试。");
    } finally {
      setPending(false);
    }
  }

  async function verifyCode(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!consentAccepted) {
      setTone("error");
      setMessage("请先阅读并同意相关条款。");
      return;
    }
    setPending(true);
    setMessage("");
    try {
      const response = await fetch("/api/auth/email/verify", {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ email, challengeId, code, consentAccepted }),
      });
      const result = (await response.json()) as ApiResult;
      if (!result.ok) {
        setTone("error");
        setMessage(result.message || "验证码无效或已过期，请重新获取。");
        return;
      }
      setTone("success");
      setMessage("验证成功，正在进入会员中心。");
      router.replace(nextPath);
      router.refresh();
    } catch {
      setTone("error");
      setMessage("验证码无效或已过期，请重新获取。");
    } finally {
      setPending(false);
    }
  }

  return (
    <main className="member-login-layout mx-auto max-w-[560px] px-5 pt-4 pb-10">
      <section className="relative overflow-hidden rounded-[20px] bg-forest-900 px-5 pt-7 pb-10 text-paper-inverse shadow-[0_18px_44px_rgba(16,40,27,0.2)]">
        <div aria-hidden="true" className="pointer-events-none absolute top-0 right-0 size-36 rounded-full border border-gilt/20" />
        <div aria-hidden="true" className="pointer-events-none absolute top-16 right-6 size-24 rounded-full border border-gilt/15" />
        <p className="font-mono text-[10px] tracking-[0.2em] text-gilt">MEMBER ARCHIVE / 会员档案</p>
        <p className="mt-3 max-w-[12em] font-display text-[25px] leading-[1.45] font-semibold">买过一次，就开始记录你的山林档案</p>
        <p className="mt-3 max-w-[28em] text-xs leading-5 text-paper-inverse/65">订单、积分、地址与邀请记录，统一归入你的专属会员页。</p>
      </section>

      <section className="relative -mt-4 rounded-[18px] border border-line/80 bg-card p-5 shadow-[0_18px_42px_rgba(42,35,22,0.12)]">
        <div className="flex items-center gap-3 border-b border-line pb-4">
          <span className="flex size-10 items-center justify-center rounded-lg bg-forest-100 text-forest-700">
            <Mail className="size-5 stroke-[1.5]" aria-hidden="true" />
          </span>
          <div>
            <h1 className="font-display text-xl leading-7 font-semibold text-ink">
              邮箱登录
            </h1>
            <p className="mt-1 text-xs leading-[18px] text-ink-3">
              首次验证将自动创建会员账户
            </p>
          </div>
        </div>

        <form onSubmit={requestCode} className="mt-4">
          <label htmlFor="member-email" className="text-xs leading-[18px] text-ink-2">
            邮箱地址
          </label>
          <input
            id="member-email"
            name="email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(event) => {
              setEmail(event.target.value);
              setChallengeId("");
              setCode("");
              setDebugCode("");
              setPolicies([]);
              setConsentAccepted(false);
            }}
            className="mt-2 h-12 w-full rounded-lg border border-line bg-paper px-4 text-[15px] leading-6 text-ink outline-none placeholder:text-ink-3 focus-visible:border-forest-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-500"
            placeholder="name@example.com"
          />
          <Button
            type="submit"
            variant="brand"
            disabled={pending || countdown > 0}
            className="mt-4 w-full"
          >
            {countdown > 0 ? `${countdown}s 后可重新发送` : "发送验证码"}
          </Button>
        </form>

        {challengeId ? (
          <form onSubmit={verifyCode} className="mt-5 border-t border-line pt-5">
            <label htmlFor="member-code" className="text-xs leading-[18px] text-ink-2">
              6 位验证码
            </label>
            <input
              id="member-code"
              name="code"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              required
              value={code}
              onChange={(event) => setCode(event.target.value.replace(/\D/g, "").slice(0, 6))}
              className="mt-2 h-12 w-full rounded-lg border border-line bg-paper px-4 font-mono text-[18px] tracking-[0.18em] text-ink outline-none focus-visible:border-forest-500 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-forest-500"
            />
            {debugCode ? (
              <p data-testid="mock-otp" className="mt-2 text-xs leading-[18px] text-warn">
                本地测试验证码：<span className="font-mono">{debugCode}</span>
              </p>
            ) : null}
            <Button
              type="submit"
              variant="brand"
              disabled={pending || code.length !== 6}
              className="mt-4 w-full"
            >
              登录并继续
            </Button>
          </form>
        ) : null}

        {challengeId && policies?.length === 2 ? (
          <div className="mt-5 border-t border-line pt-4 text-xs leading-5 text-ink-2">
            <p className="font-semibold text-ink">本次同意版本</p>
            <ul className="mt-2 grid gap-1">
              {policies.map((policy) => (
                <li key={policy.kind}>
                  <a
                    href={policy.href}
                    target="_blank"
                    rel="noreferrer"
                    className="text-forest-700 underline underline-offset-4"
                  >
                    {policy.kind === "terms" ? "《用户协议》" : "《隐私政策》"}
                    （{policy.language.toUpperCase()} · {policy.version}）
                  </a>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <label className="mt-5 flex min-h-11 cursor-pointer items-start gap-3 text-xs leading-[18px] text-ink-2">
          <input
            type="checkbox"
            checked={consentAccepted}
            disabled={!challengeId || policies?.length !== 2}
            onChange={(event) => setConsentAccepted(event.target.checked)}
            className="mt-0.5 size-5 accent-forest-700 disabled:opacity-50"
          />
          <span>我已阅读并同意《用户协议》与《隐私政策》</span>
        </label>
        {policyNotice ? (
          <p
            className={`mt-2 text-[11px] leading-[1.6] ${
              policyBundlePending ? "text-ink-3" : "text-warn"
            }`}
          >
            {policyNotice}
          </p>
        ) : null}

        <p
          aria-live="polite"
          className={`mt-4 min-h-[18px] text-xs leading-[18px] ${
            tone === "success" ? "text-success" : "text-danger"
          }`}
        >
          {message}
        </p>
      </section>

      <section className="mt-5 rounded-[16px] border border-line/80 bg-card p-4 shadow-[0_10px_28px_rgba(42,35,22,0.07)]">
        <div className="flex items-center gap-3">
          <Smartphone className="size-5 stroke-[1.5] text-forest-500" aria-hidden="true" />
          <div>
            <p className="text-sm font-semibold text-ink">使用手机号登录</p>
            <p className="mt-1 text-xs leading-[18px] text-ink-3">
              支持国际手机号，正式通道开通后开放。
            </p>
          </div>
        </div>
      </section>

      <section className="mt-8 border-y border-line py-5">
        <p className="font-mono text-[10px] tracking-[0.15em] text-ink-3">YOUR MEMBER FILE</p>
        <div className="mt-4 grid grid-cols-3 gap-3">
          {[
            [FileClock, "订单归档"],
            [Coins, "积分记录"],
            [UserRoundPlus, "邀请关系"],
          ].map(([Icon, label]) => (
            <div key={String(label)} className="min-w-0 text-center">
              <Icon className="mx-auto size-5 text-forest-500" aria-hidden="true" />
              <p className="mt-2 text-xs leading-5 text-ink-2">{String(label)}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
